import { BRAND } from '../brand'
import { formatPercent } from '../utils/formatINR'
import type { CardModel } from './model'

/**
 * Every word the share card draws, in one place.
 *
 * Two reasons this is a module rather than string literals inside `paint.ts`.
 *
 * The first is legal. The card is a marketing communication that travels away
 * from the site, so it is the only thing a viewer on Instagram ever reads — the
 * footer's disclaimers do not come with it. Both disclaimer lines below are
 * required, and neither is decorative: see the note on `DISCLAIMER`. Copy that
 * carries a compliance obligation should be somewhere a reviewer can find all of
 * it at once, not scattered through drawing code.
 *
 * The second is typographic. Every glyph the card draws has to exist in the
 * subsetted webfonts, and the test in `card.test.ts` enforces that by parsing the
 * `unicode-range` declarations out of `src/styles/fonts.css` and checking every
 * string here against them. That test only works if the strings are enumerable.
 */

/**
 * The qualification, carried on the card itself.
 *
 * **Line 1** is verbatim from `src/components/Headline.tsx`, where a comment
 * explains it sits with the claim because the CCPA Guidelines, 2022 measure a
 * disclaimer by whether a normally-sighted person reading once would see it.
 *
 * **Line 2 exists because the card leaves the page.** In the app, "not investment
 * advice / not registered with SEBI" lives in the footer. A card carrying a
 * quantified probability of investment success, the wordmark and the domain, with
 * no such line, is the whole product's claim surface compressed into one image
 * with the advice disclaimer missing. Regulation 3(1) of the SEBI (Investment
 * Advisers) Regulations, 2013 makes "holding out" a limb independent of actually
 * advising, which makes marketing artefacts part of the exposure and not just the
 * software. Five words close it.
 *
 * Do not drop either line to win vertical space, and do not move them away from
 * the number they qualify.
 */
export const DISCLAIMER: readonly [string, string] = [
  'An illustration from assumptions you can change — not a forecast.',
  'Not investment advice. Not SEBI-registered.',
]

/** Matches the app's headline eyebrow exactly, so the card reads as the same thing. */
export function eyebrow(targetAge: number): string {
  return `chance of FIRE by ${Math.round(targetAge)}`
}

/**
 * Alt text for the card preview, and the body of the share sheet.
 *
 * Deliberately NOT `FanChart`'s `describe()`, which is otherwise the obvious
 * thing to reuse. That function names the median corpus and the requirement in
 * rupees — the two figures the card exists to leave out. Passing it to
 * `navigator.share({ text })` would paste them into the post body and undo the
 * privacy decision through a side door.
 */
export function cardAltText(model: CardModel): string {
  const probability =
    model.probability === null ? '' : `${formatPercent(model.probability)} `
  // Guarded like cardModelFrom and fanYMax already are. Without it an empty
  // projection puts "projected from age NaN" into the text handed to
  // navigator.share — i.e. straight into the user's WhatsApp composer.
  const from =
    model.ages.length === 0 ? '' : `projected from age ${Math.round(model.ages[0])}. `
  return (
    `${probability}chance of reaching financial independence by age ${Math.round(model.targetAge)}. ` +
    from +
    `${DISCLAIMER[0]} ${DISCLAIMER[1]}`
  )
}

export const SHARE_TITLE = `${BRAND.name} — ${BRAND.tagline}`

/**
 * The text that rides along with the image in the share sheet.
 *
 * `BRAND.url` and nothing else. The plan-encoded fragment that `encodeScenarios`
 * produces carries age, corpus, monthly investment and expenses in a decodable
 * form, and attaching it to a card that deliberately shows no rupee figures would
 * be worse than either choice alone — the viewer sees no numbers while the link
 * quietly hands over all of them.
 */
export function shareText(model: CardModel): string {
  return `${cardAltText(model)}\n${BRAND.url}`
}

/**
 * Every fixed string the card can draw. The unicode-coverage test iterates this;
 * the variable parts (percentages, ages) are digits, `%` and spaces, all of which
 * are inside the latin subset by construction.
 */
export const CARD_STATIC_STRINGS: readonly string[] = [
  ...DISCLAIMER,
  eyebrow(45),
  BRAND.name,
  BRAND.shareUrl,
  BRAND.tagline,
]
